import type {ThreadMessageLike} from '@assistant-ui/core';

// oxlint-disable-next-line wandb/no-deprecated-imports
import type {Message, MessageContentPart} from './providerMessage';
import type {
  ContentPart as StoreContentPart,
  MessageNode,
} from './store/thread';

/**
 * toAUIMessage converts a store `MessageNode` to a `ThreadMessageLike` for
 * `assistant-ui`.
 */
export function toAUIMessage(node: MessageNode): ThreadMessageLike {
  const content: AUIContentPart[] = [];
  for (const part of node.content) {
    content.push(...toAUIContentParts(part));
  }

  return {
    id: node.id,
    role: node.role,
    content,
    createdAt: new Date(node.createdAt),
  };
}

/** Convert a store `MessageNode` to a `Message` for LLM consumption. */
export function toRuntimeMessage(node: MessageNode): Message {
  const parts: MessageContentPart[] = [];

  for (const part of node.content) {
    switch (part.type) {
      case 'text':
        parts.push({type: 'text', text: part.text});
        break;

      case 'thinking':
        break; // Thinking is omitted from runtime messages (not sent to LLM)

      case 'tool-call':
        parts.push({
          type: 'tool-call',
          toolCallId: part.toolCallId,
          toolName: part.toolName,
          args: part.args ?? {},
        });
        if (part.result !== undefined) {
          parts.push({
            type: 'tool-result',
            toolCallId: part.toolCallId,
            content: part.result,
            isError: part.isError ?? false,
          });
        }
        break;
    }
  }

  return {role: node.role, content: parts};
}

type AUIContentPart = Extract<
  ThreadMessageLike['content'],
  readonly unknown[]
>[number];

function toAUIContentParts(part: StoreContentPart): AUIContentPart[] {
  switch (part.type) {
    case 'text':
      return [{type: 'text', text: part.text}];

    case 'thinking':
      return [{type: 'reasoning', text: part.text}];

    case 'tool-call':
      return [
        {
          type: 'tool-call',
          toolCallId: part.toolCallId,
          toolName: part.toolName,
          argsText: part.argsText,
          args: part.args ?? {},
          ...(part.result !== undefined ? {result: part.result} : {}),
          ...(part.isError !== undefined ? {isError: part.isError} : {}),
        },
      ];
  }
}
